import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DropdownButton } from '@/tiptap/tiptap-ui/ui/Dropdown'
import { Icon } from '@/tiptap/tiptap-ui/ui/Icon'
import { Surface } from '@/tiptap/tiptap-ui/ui/Surface'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Command, MenuListProps } from './types'

export const MenuList = React.forwardRef((props: MenuListProps, ref) => {
  const scrollContainer = useRef<HTMLDivElement>(null)
  const activeItem = useRef<HTMLButtonElement>(null)
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0)
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0)

  // Anytime the groups change, i.e. the user types to narrow it down, we want to
  // reset the current selection to the first menu item
  useEffect(() => {
    setSelectedGroupIndex(0)
    setSelectedCommandIndex(0)
  }, [props.items])

  const selectItem = useCallback(
    (groupIndex: number, commandIndex: number) => {
      const command = props.items[groupIndex].commands[commandIndex]
      props.command(command)
    },
    [props]
  )

  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
      if (event.key === 'ArrowDown') {
        if (!props.items.length) {
          return false
        }

        const commands = props.items[selectedGroupIndex].commands

        let newCommandIndex = selectedCommandIndex + 1
        let newGroupIndex = selectedGroupIndex

        if (commands.length - 1 < newCommandIndex) {
          newCommandIndex = 0
          newGroupIndex = selectedGroupIndex + 1
        }

        if (props.items.length - 1 < newGroupIndex) {
          newGroupIndex = 0
        }

        setSelectedCommandIndex(newCommandIndex)
        setSelectedGroupIndex(newGroupIndex)

        return true
      }

      if (event.key === 'ArrowUp') {
        if (!props.items.length) {
          return false
        }

        let newCommandIndex = selectedCommandIndex - 1
        let newGroupIndex = selectedGroupIndex

        if (newCommandIndex < 0) {
          newGroupIndex = selectedGroupIndex - 1
          newCommandIndex = props.items[newGroupIndex]?.commands.length - 1 || 0
        }

        if (newGroupIndex < 0) {
          newGroupIndex = props.items.length - 1
          newCommandIndex = props.items[newGroupIndex].commands.length - 1
        }

        setSelectedCommandIndex(newCommandIndex)
        setSelectedGroupIndex(newGroupIndex)

        return true
      }

      if (event.key === 'Enter') {
        if (
          !props.items.length ||
          selectedGroupIndex === -1 ||
          selectedCommandIndex === -1
        ) {
          return false
        }

        selectItem(selectedGroupIndex, selectedCommandIndex)

        return true
      }

      return false
    },
  }))

  useEffect(() => {
    if (activeItem.current && scrollContainer.current) {
      // Find the ScrollArea's viewport element
      const scrollAreaViewport = scrollContainer.current.querySelector(
        '[data-radix-scroll-area-viewport]'
      )

      if (scrollAreaViewport) {
        const offsetTop = activeItem.current.offsetTop
        const offsetHeight = activeItem.current.offsetHeight
        const viewportHeight = scrollAreaViewport.clientHeight
        const currentScrollTop = scrollAreaViewport.scrollTop

        // Check if the active item is outside the visible area
        const itemTop = offsetTop
        const itemBottom = offsetTop + offsetHeight
        const visibleTop = currentScrollTop
        const visibleBottom = currentScrollTop + viewportHeight

        // Scroll to show the item if it's not fully visible
        if (itemTop < visibleTop) {
          // Item is above visible area, scroll up
          scrollAreaViewport.scrollTop = itemTop - 8 // 8px padding
        } else if (itemBottom > visibleBottom) {
          // Item is below visible area, scroll down
          scrollAreaViewport.scrollTop = itemBottom - viewportHeight + 8 // 8px padding
        }
      }
    }
  }, [selectedCommandIndex, selectedGroupIndex])

  const createCommandClickHandler = useCallback(
    (groupIndex: number, commandIndex: number) => {
      return () => {
        selectItem(groupIndex, commandIndex)
      }
    },
    [selectItem]
  )

  if (!props.items.length) {
    return null
  }

  return (
    <Surface ref={scrollContainer} className='flex-wrap' withBorder={false}>
      <ScrollArea
        className='slash-command-scroll-area h-full p-2'
        type='hover'
        viewClassName='max-h-[384px] h-full'
      >
        <div className='grid grid-cols-1 gap-0.5'>
          {props.items.map((group, groupIndex: number) => (
            <React.Fragment key={`${group.title}-wrapper`}>
              <div
                className='col-[1/-1] mx-2 my-1 text-[0.65rem] font-semibold tracking-wider text-[var(--muted-foreground)] uppercase select-none'
                key={`${group.title}`}
              >
                {group.title}
              </div>
              {group.commands.map((command: Command, commandIndex: number) => (
                <DropdownButton
                  key={`${command.label}`}
                  ref={
                    selectedGroupIndex === groupIndex &&
                    selectedCommandIndex === commandIndex
                      ? activeItem
                      : null
                  }
                  isActive={
                    selectedGroupIndex === groupIndex &&
                    selectedCommandIndex === commandIndex
                  }
                  className='text-foreground'
                  onClick={createCommandClickHandler(groupIndex, commandIndex)}
                >
                  <Icon name={command.iconName} className='mr-1' />
                  {command.label}
                </DropdownButton>
              ))}
            </React.Fragment>
          ))}
        </div>
      </ScrollArea>
    </Surface>
  )
})

MenuList.displayName = 'MenuList'

export default MenuList
